import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { apiRequest } from '@/lib/api';

export default function SimpleTestPage() {
  const [url, setUrl] = useState("https://www.airbnb.com/rooms/1283260472280128884?guests=1&adults=1&s=67&unique_share_id=f84e8e50-5152-4ee7-8777-471cb24d4bb2");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<string | null>(null);

  const handleTest = async () => {
    if (!url) {
      setError("Please enter an Airbnb URL");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      console.log("Sending test request with URL:", url);
      
      // Try the debug endpoint which doesn't require auth
      const data = { url };
      const response = await apiRequest('POST', 'debug/scrape-listing', data);
      
      console.log("Response status:", response.status);
      // Convert headers to an object safely
      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      console.log("Response headers:", headers);
      
      const text = await response.text();
      console.log("Response text (first 200 chars):", text.substring(0, 200));
      
      setResponse(text);
      
    } catch (err: any) {
      console.error("Test error:", err);
      setError(err.message || "Failed to make request");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-10">
      <h1 className="text-3xl font-bold mb-6">Simple Scraper Test</h1>
      <p className="text-muted-foreground mb-8">
        This is a simplified page to test the scraper functionality with minimal complexity.
      </p>

      <div className="grid gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Test API Endpoint</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <label htmlFor="url" className="font-medium">Airbnb URL</label>
                <div className="flex gap-2">
                  <Input
                    id="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.airbnb.com/rooms/..."
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleTest}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Testing...
                      </>
                    ) : "Test API"}
                  </Button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {response && (
                <div className="mt-4">
                  <h3 className="font-semibold mb-2">Raw Response:</h3>
                  <Textarea 
                    className="font-mono text-xs h-[300px] w-full" 
                    readOnly 
                    value={response}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}